/**
 * "Esta VM é Windows?" — e por que a resposta depende do que se vai fazer com ela (#729).
 *
 * Existem DUAS perguntas, com respostas legitimamente diferentes:
 *
 * 1. **Apresentação** (RDP ou SSH, ícone, instruções de acesso). Errar aqui custa uma tela
 *    inconveniente, então vale usar toda pista disponível — inclusive o `ostype` do PVE,
 *    que é dado vivo e some quando o cluster está degradado. Ele só AFIRMA Windows; nunca
 *    nega, porque o campo ausente não é evidência de Linux.
 *
 * 2. **O que o servidor vai fazer** (o modo do formulário de reset e o aviso de reinício).
 *    Aqui a única régua válida é a do backend, porque é ela que decide o comportamento:
 *    `is_windows(None)` é `False`, e o PVE não entra na conta. Uma VM Windows legada sem
 *    template e sem pista do guest agent chega com `os_family: null`; se a tela caísse no
 *    PVE, ela mostraria "a senha é aplicada pelo agente convidado, sem reiniciar a VM"
 *    enquanto o backend a trata como Linux e REINICIA quando o guest agent não responde.
 *    Prometer o oposto do que o servidor faz é o defeito desta issue mudando de lugar.
 *
 * Por isso as duas réguas são funções separadas e nomeadas: a distinção não sobrevive a
 * um único booleano chamado `isWindows`.
 */

/** `ostype` do PVE para as famílias Windows (`pve-docs`: qm.conf, chave `ostype`). */
const OSTYPES_WINDOWS_DO_PVE = new Set([
  'win11',
  'win10',
  'win8',
  'win7',
  'wvista',
  'wxp',
  'w2k',
  'w2k3',
  'w2k8',
  'w2k12',
  'w2k16',
  'w2k19',
  'w2k22',
  'w2k25',
])

type ComFamiliaDeSO = {
  os_family?: string | null
  // `| null`, não `| unknown`: com `unknown` a união COLAPSA e a interface deixa de
  // restringir qualquer coisa (`{ proxmox_details: 42 }` passaria no type-check).
  proxmox_details?: { os_type?: string | null } | null
}

function osTypeDoPve(instance: ComFamiliaDeSO): string {
  const detalhes = instance.proxmox_details as { os_type?: string | null } | undefined
  return (detalhes?.os_type || '').toLowerCase()
}

/**
 * Régua da APRESENTAÇÃO: aceita a pista do PVE quando o backend não sabe a família.
 *
 * Use para escolher entre RDP e SSH, e para o que só muda o que o cliente lê.
 */
export function ehWindowsParaApresentacao(instance: ComFamiliaDeSO): boolean {
  if (instance.os_family) return instance.os_family === 'windows'
  return OSTYPES_WINDOWS_DO_PVE.has(osTypeDoPve(instance))
}

/**
 * Régua das CREDENCIAIS: só o backend decide.
 *
 * Use para tudo que descreve o que o servidor fará — o modo do formulário de reset e o
 * aviso de reinício. `null` (indeterminado) NÃO é Windows, exatamente como no backend.
 */
export function ehWindowsParaCredenciais(instance: ComFamiliaDeSO): boolean {
  return instance.os_family === 'windows'
}
